<template>
  <div class="gg-toolbar">
    <div class="gg-toolbar__group">
      <label class="gg-toolbar__label" for="gg-program-select">Produção:</label>
      <v-select
        id="gg-program-select"
        class="gg-select"
        :items="producoes ? producoes.items : []"
        :value="selectedProducao"
        :loading="loadingProducoes"
        item-text="nome_producao"
        item-value="cod_producao"
        return-object
        dense
        flat
        solo
        hide-details
        append-icon="mdi-menu-down"
        :placeholder="loadingProducoes ? 'Carregando programas…' : 'Selecione um programa'"
        aria-label="Selecione o programa"
        :menu-props="{ contentClass: 'gg-select-menu', offsetY: true, nudgeBottom: 4 }"
        @change="onChangeProducao"
      > 
        <template #selection="{ item }">
          <span class="gg-select__value">{{ item.nome_producao || item.sig_producao }}</span>
        </template>
        <template #item="{ item }">
          <span class="gg-select__option">{{ item.nome_producao || item.sig_producao }}</span>
        </template>
      </v-select>

      <!-- Site selector — resolves only after a program is selected, so it
           is absent in the empty state. Kept here for the loaded workflow. -->
      <template v-if="selectedProducao && mySites.length">
        <label class="gg-toolbar__label ml-8">Site</label>
        <v-select
          class="gg-select gg-select--praca"
          :value="selectedSiteSigla"
          :items="mySites"
          item-text="sigla"
          item-value="sigla"
          dense
          flat
          solo
          hide-details
          append-icon="mdi-menu-down"
          aria-label="Selecione o site"
          :menu-props="{ contentClass: 'gg-select-menu', offsetY: true, nudgeBottom: 4 }"
          @change="selectSite"
        >
          <template #selection="{ item }">
            <span class="gg-select__value">{{ item.sigla || item }}</span>
          </template>
          <template #item="{ item }">
            <span class="gg-select__option">{{ item.sigla || item }}</span>
          </template>
        </v-select>
      </template>
    </div>

    <!-- The link is a per-production service token, so it only exists once a
         program is chosen. -->
    <GloboGraphicsPreviewLink
      v-if="selectedProducao"
      :cod-producao="selectedProducao.cod_producao"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GloboGraphicsPreviewLink from "./GloboGraphicsPreviewLink.vue";

export default {
  name: "GloboGraphicsToolbar",
  components: { GloboGraphicsPreviewLink },
  data() {
    return {
      loadingProducoes: false,
    };
  },
  computed: {
    ...mapGetters({
      producoes: "producoes/getterProducoes",
      isAdm: "users/isAdm",
      username: "users/username",
    }),
    ...mapGetters("sites", {
      mySites: "getterMySites",
      selectedSiteSigla: "getterSelectedSiteSigla",
    }),
    selectedProducao() {
      return this.$store.state.producoes.selectedProducao || null;
    },
  },
  async created() {
    // Ensure the program list is available and restore the persisted selection —
    // mirrors ProducaoBar, which previously owned this for the GG route.
    const existing = this.producoes?.items;
    if (!existing || existing.length === 0) {
      this.loadingProducoes = true;
      try {
        await this.getProducoes({
          page: 1,
          filtro_texto: "",
          flg_ativo: "S",
          isAdmin: this.isAdm,
          dsc_login_usuario: this.username,
        });

        const storedProd = Number(localStorage.getItem("@socialcam/selected_option_production"));
        if (storedProd && this.producoes?.items && !this.selectedProducao) {
          const match = this.producoes.items.find((p) => p.cod_producao === storedProd);
          if (match) await this.setSelectedProducao(match);
        }
      } finally {
        this.loadingProducoes = false;
      }
    }
  },
  methods: {
    ...mapActions({
      getProducoes: "producoes/getProducoes",
      setSelectedProducao: "producoes/setSelectedProducao",
    }),
    ...mapActions("sites", ["selectSite"]),
    async onChangeProducao(producao) {
      if (!producao) return;
      localStorage.setItem("@socialcam/selected_option_production", producao.cod_producao);
      // Adopt the production as-is; GloboGraphicsView watches selectedProducao and
      // loads the site + workflow from here.
      await this.setSelectedProducao(producao);
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
  padding: var(--sc-space-6);
  background: var(--sc-gg-surface-2);
}

.gg-toolbar__group {
  display: flex;
  align-items: center;
  gap: var(--sc-space-2);
  min-width: 0;
}

.gg-toolbar__label {
  margin: 0;
  color: var(--sc-gg-text-secondary);
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.07px;
  white-space: nowrap;
}

/* Program / site select — matched to the GG design input */
.gg-select {
  flex: 0 1 256px;
  min-width: 150px;
  max-width: 256px;
}

.gg-select--praca {
  flex: 0 1 256px;
  max-width: 256px;
}

.gg-select ::v-deep .v-input__slot {
  min-height: 48px;
  padding: 0 var(--sc-space-3) !important;
  background: var(--sc-gg-surface-1) !important;
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
  box-shadow: none !important;
}

.gg-select ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  min-width: 0;
}

.gg-select ::v-deep .v-icon {
  color: var(--sc-gg-text-secondary) !important;
}

.gg-select ::v-deep input::placeholder {
  color: var(--sc-gg-text-tertiary) !important;
}

.gg-select__value {
  overflow: hidden;
  color: var(--sc-gg-text-primary);
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.08px;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>

<!-- Non-scoped: the v-select menu is teleported to the app root, so its content
     class cannot be reached by scoped selectors. -->
<style lang="scss">
.gg-select-menu {
  // Menu is teleported outside .gg-shell, so it needs the module font here too.
  font-family: "Inter", "Globotipo", sans-serif;
  background: var(--sc-gg-surface-1) !important;
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
  box-shadow: var(--sc-gg-shadow-low) !important;

  .v-list {
    padding: var(--sc-space-1);
    background: transparent;
  }

  .v-list-item {
    min-height: 32px;
    border-radius: var(--sc-radius-sm);
  }

  .v-list-item--active,
  .v-list-item:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .gg-select__option {
    color: var(--sc-gg-text-strong);
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.07px;
  }
}
</style>
